import {
  DataBlockFieldDto,
  DataBlockTypeDto,
  DataBlockVariant,
  DataType,
  QueryFactory,
  SortDirection,
  UIConfigDto,
  UpdateDataBlockDto,
} from '@lmt/shared-types';
import { ClientFactory } from '@lmt/unicorn-shared/app/api/client-factory';
import { parseError } from '@lmt/unicorn-shared/app/helpers/error-handling';
import { UniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { QueryClient } from '@tanstack/react-query';

import {
  checkUIConfigContainsField,
  getDataBlockTypeNameForLocalization,
} from '../common';
import { getConfigForField, isEmpty } from './common';
import { useModal } from '../../common/ui-kit/modal/useModal';

// select value from previous blocks
async function getValueFromPreviousDataBlocks(
  patientId: number,
  configData: UIConfigDto,
  field: DataBlockFieldDto,
  // open CopyDataBlockFieldValueForm
  openCopyDataBlockValue: (
    patientId: number,
    typeId: number,
    fieldId: number,
    config: string,
  ) => Promise<string | undefined>,
) {
  let config = configData.view;
  if (checkUIConfigContainsField(configData.details, field.name)) {
    config = configData.details;
  }
  const fieldConfig = getConfigForField(
    config,
    field.name,
    field.dataType === DataType.Table,
  );
  if (fieldConfig && patientId) {
    return await openCopyDataBlockValue(
      patientId,
      field.dataBlockTypeId,
      field.id,
      fieldConfig,
    );
  } else {
    return undefined;
  }
}

function checkIfPreviousValuesExist(values: any[], fieldName: string) {
  if (isEmpty(values)) {
    return false;
  }
  return values.some((value) => !isEmpty(value[fieldName]));
}

async function getValuesFromPreviousDataBlocks(
  patientId: number,
  currentValues: any,
  configData: UIConfigDto,
  fields: DataBlockFieldDto[],
  previosValues: any,
  previousValuesArray: any[],
  openCopyDataBlockValue: (
    patientId: number,
    typeId: number,
    fieldId: number,
    config: string,
  ) => Promise<string | undefined>,
) {
  const valuesDictionary: { [key: string]: string | undefined } = currentValues
    ? JSON.parse(JSON.stringify(currentValues))
    : {};
  for (const field of fields) {
    if (
      isEmpty(currentValues[field.name]) &&
      (checkUIConfigContainsField(configData.view, field.name) ||
        checkUIConfigContainsField(configData.details, field.name))
    ) {
      if (isEmpty(previosValues[field.name])) {
        if (checkIfPreviousValuesExist(previousValuesArray, field.name)) {
          const value = await getValueFromPreviousDataBlocks(
            patientId,
            configData,
            field,
            openCopyDataBlockValue,
          );
          if (!isEmpty(value)) {
            valuesDictionary[field.name] = value!;
          }
        }
      } else {
        valuesDictionary[field.name] = previosValues[field.name];
      }
    }
  }
  return valuesDictionary;
}

async function getAllDataBlocksValues(
  patientId: number,
  dataBlockTypeId: number,
) {
  const dataBlocks = await QueryFactory.DataBlockClient.getWithValues(
    patientId,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    dataBlockTypeId,
  );
  return dataBlocks.data
    .filter((dataBlock) => !isEmpty(dataBlock.values))
    .map((dataBlock) => dataBlock.values);
}

async function getAllCasesValues(patientId: number) {
  const cases = await QueryFactory.CaseClient.getCases(
    patientId,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    true,
  );
  return cases.data
    .filter((pCase) => !isEmpty(pCase.values))
    .map((pCase) => pCase.values);
}

async function getAllVisitsValues(patientId: number) {
  const visits = await QueryFactory.AppointmentEventsClient.searchVisits(
    patientId,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    true,
  );
  return visits
    .filter((visit) => !isEmpty(visit.values))
    .map((visit) => visit.values);
}

async function getSharedFieldsBasedOnPrevious(
  dataBlockTypes: DataBlockTypeDto[],
  dataBlockTypeValue: string,
) {
  const sharedDataBlockTypeId = dataBlockTypes.find(
    (type) =>
      type.variant === DataBlockVariant.SharedFields &&
      type.value === dataBlockTypeValue,
  )!.id;
  return await getFieldsBasedOnPrevious(sharedDataBlockTypeId);
}

async function getFieldsBasedOnPrevious(dataBlockTypeId: number) {
  const dataBlockFields =
    await QueryFactory.DataBlockFieldClient.get(dataBlockTypeId);
  return dataBlockFields.filter(
    (field) => field.isBasedOnPrevious && !field.isDeactivated,
  );
}

async function getPreviousDataBlock(
  patientId: number,
  dataBlockTypeId: number,
  dataBlockId?: number,
) {
  const dataBlocks = await QueryFactory.DataBlockClient.getWithValues(
    patientId,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    dataBlockTypeId,
    undefined,
    undefined,
    undefined,
    0,
    2,
    'id',
    SortDirection.Desc,
  );
  const previousBlocks = dataBlocks.data.filter(
    (block) => block.id !== dataBlockId,
  );
  return previousBlocks[0] ?? null;
}

async function getDataBlockValues(
  patientId: number,
  dataBlockTypeId: number,
  configData: UIConfigDto,
  currentValues: any,
  previousValues: any,
  openCopyDataBlockValue: (
    patientId: number,
    typeId: number,
    fieldId: number,
    config: string,
  ) => Promise<string | undefined>,
) {
  const dataBlockFieldsBasedOnPrevious =
    await getFieldsBasedOnPrevious(dataBlockTypeId);
  if (isEmpty(dataBlockFieldsBasedOnPrevious)) {
    return {};
  }
  const allDataBlockValues = await getAllDataBlocksValues(
    patientId,
    dataBlockTypeId,
  );
  return await getValuesFromPreviousDataBlocks(
    patientId,
    currentValues,
    configData,
    dataBlockFieldsBasedOnPrevious,
    previousValues,
    allDataBlockValues,
    openCopyDataBlockValue,
  );
}

async function getCaseValues(
  patientId: number,
  caseId: number,
  previousCaseId: number,
  configData: UIConfigDto,
  dataBlockTypes: DataBlockTypeDto[],
  openCopyDataBlockValue: (
    patientId: number,
    typeId: number,
    fieldId: number,
    config: string,
  ) => Promise<string | undefined>,
  onlyChanged?: boolean,
) {
  const caseSharedValues = (await QueryFactory.CaseClient.getCase(caseId))
    .values;
  const sharedFieldsBasedOnPrevious = await getSharedFieldsBasedOnPrevious(
    dataBlockTypes,
    'Case',
  );
  if (isEmpty(sharedFieldsBasedOnPrevious)) {
    return undefined;
  }
  const previousCase = await QueryFactory.CaseClient.getCase(previousCaseId);
  const allCasesValues = await getAllCasesValues(patientId);
  const values = await getValuesFromPreviousDataBlocks(
    patientId,
    caseSharedValues,
    configData,
    sharedFieldsBasedOnPrevious,
    previousCase.values,
    allCasesValues,
    openCopyDataBlockValue,
  );
  if (onlyChanged) {
    if (JSON.stringify(values) === JSON.stringify(caseSharedValues)) {
      return undefined;
    } else {
      return values;
    }
  } else {
    return values;
  }
}

async function getVisitValues(
  patientId: number,
  visitId: number,
  previousVisitId: number,
  configData: UIConfigDto,
  dataBlockTypes: DataBlockTypeDto[],
  openCopyDataBlockValue: (
    patientId: number,
    typeId: number,
    fieldId: number,
    config: string,
  ) => Promise<string | undefined>,
  onlyChanged?: boolean,
) {
  const visitSharedValues = (
    await QueryFactory.AppointmentEventsClient.getVisit(visitId)
  ).values;
  const sharedFieldsBasedOnPrevious = await getSharedFieldsBasedOnPrevious(
    dataBlockTypes,
    'Visit',
  );
  if (isEmpty(sharedFieldsBasedOnPrevious)) {
    return undefined;
  }
  const previousVisit =
    await QueryFactory.AppointmentEventsClient.getVisit(previousVisitId);
  const allVisitsValues = await getAllVisitsValues(patientId);
  const values = await getValuesFromPreviousDataBlocks(
    patientId,
    visitSharedValues,
    configData,
    sharedFieldsBasedOnPrevious,
    previousVisit.values,
    allVisitsValues,
    openCopyDataBlockValue,
  );
  if (onlyChanged) {
    if (JSON.stringify(values) === JSON.stringify(visitSharedValues)) {
      return undefined;
    } else {
      return values;
    }
  } else {
    return values;
  }
}

function getCopyValueFormWithLocalization(
  dataBlockType: DataBlockTypeDto,
  openCopyDataBlockValue: (
    patientId: number,
    typeId: number,
    fieldId: number,
    config: string,
    typeName?: string,
  ) => Promise<string | undefined>,
) {
  const dataBlockTypeNameForLocalization =
    getDataBlockTypeNameForLocalization(dataBlockType);
  return (patientId: number, typeId: number, fieldId: number, config: string) =>
    openCopyDataBlockValue(
      patientId,
      typeId,
      fieldId,
      config,
      dataBlockTypeNameForLocalization,
    );
}

// for existing data block copy values from previous data blocks
// with the same patientId and dataBlockTypeId
// only for based on previous fields
export async function copyPreviousValues(
  dataBlockId: number,
  openCopyDataBlockValue: (
    patientId: number,
    typeId: number,
    fieldId: number,
    config: string,
    typeName?: string,
  ) => Promise<string | undefined>,
  modals: ReturnType<typeof useModal>,
  queryClient: QueryClient,
) {
  try {
    const dataBlock =
      await QueryFactory.DataBlockClient.getDataBlock(dataBlockId);
    const patientId = dataBlock.patientId;
    const caseId = dataBlock.caseId;
    if (!dataBlock || !patientId || !caseId) {
      return;
    }
    const dataBlockTypeId = dataBlock.typeId;
    const dataBlockTypes = await QueryFactory.DataBlockTypeClient.get();
    const dataBlockType = dataBlockTypes.find(
      (blockType) => blockType.id === dataBlockTypeId,
    );
    if (!dataBlockType) {
      return;
    }
    const previousDataBlock = await getPreviousDataBlock(
      patientId,
      dataBlockTypeId,
      dataBlockId,
    );
    if (!previousDataBlock) {
      return;
    }
    const configData =
      await QueryFactory.DataBlockTypeClient.getUIConfigs(dataBlockTypeId);
    const openCopyValueForm = getCopyValueFormWithLocalization(
      dataBlockType,
      openCopyDataBlockValue,
    );
    const values = await getDataBlockValues(
      patientId,
      dataBlockTypeId,
      configData,
      dataBlock.values,
      previousDataBlock.values,
      openCopyValueForm,
    );
    const updatedDataBlock: UpdateDataBlockDto = {
      values: values as any,
    };
    if (dataBlockType.variant === DataBlockVariant.Case) {
      const sharedValues = await getCaseValues(
        patientId,
        caseId,
        previousDataBlock.caseId!,
        configData,
        dataBlockTypes,
        openCopyValueForm,
        true,
      );
      if (sharedValues) {
        updatedDataBlock.updateCaseDto = {
          values: sharedValues,
        };
      }
    }
    if (dataBlockType.variant === DataBlockVariant.Visit) {
      const sharedValues = await getVisitValues(
        patientId,
        dataBlock.visitId!,
        previousDataBlock.visitId!,
        configData,
        dataBlockTypes,
        openCopyValueForm,
        true,
      );
      if (sharedValues) {
        updatedDataBlock.updateVisitDto = {
          values: sharedValues,
        };
      }
    }
    if (
      (Object.keys(values).length === 0 ||
        JSON.stringify(dataBlock.values) === JSON.stringify(values)) &&
      !updatedDataBlock.updateCaseDto &&
      !updatedDataBlock.updateVisitDto
    ) {
      return;
    }
    await ClientFactory.DataBlockClient.edit(dataBlockId, updatedDataBlock);
    await queryClient.invalidateQueries({
      queryKey: QueryFactory.DataBlockQuery.getDataBlockQueryKey(dataBlockId),
    });
    await queryClient.invalidateQueries({
      queryKey: QueryFactory.DataBlockQuery.getQueryKey({ caseId }),
    });
    await queryClient.invalidateQueries({
      queryKey: QueryFactory.DataBlockQuery.getWithValuesQueryKey(),
    });
    return true;
  } catch (error) {
    await modals.showError({ text: parseError(error) });
  }
}

// get data block values and shared values from existing data blocks
// for given patientId and dataBlockTypeId
// only for based on previous field
export async function getPreviousDataBlockValues(
  patientId: number,
  dataBlockTypeId: number,
  openCopyDataBlockValue: (
    patientId: number,
    typeId: number,
    fieldId: number,
    config: string,
    typeName?: string,
  ) => Promise<string | undefined>,
  setValues: (value: { [key: string]: string | undefined }) => void,
  setSharedValues: (value: { [key: string]: string | undefined }) => void,
  modals: ReturnType<typeof useModal>,
  caseId: number,
  visitId?: number,
) {
  try {
    const dataBlockTypes = await QueryFactory.DataBlockTypeClient.get();
    const dataBlockType = dataBlockTypes.find(
      (blockType) => blockType.id === dataBlockTypeId,
    );
    if (!dataBlockType) {
      return;
    }
    const previousDataBlock = await getPreviousDataBlock(
      patientId,
      dataBlockTypeId,
    );
    if (!previousDataBlock) {
      return;
    }
    const configData =
      await QueryFactory.DataBlockTypeClient.getUIConfigs(dataBlockTypeId);
    const openCopyValueForm = getCopyValueFormWithLocalization(
      dataBlockType,
      openCopyDataBlockValue,
    );
    const values = await getDataBlockValues(
      patientId,
      dataBlockTypeId,
      configData,
      {},
      previousDataBlock.values,
      openCopyValueForm,
    );
    if (values && Object.keys(values).length > 0) {
      setValues(values);
    }
    if (dataBlockType.variant === DataBlockVariant.Case) {
      const caseSharedValues = await getCaseValues(
        patientId,
        caseId,
        previousDataBlock.caseId!,
        configData,
        dataBlockTypes,
        openCopyValueForm,
      );
      if (caseSharedValues) {
        setSharedValues(caseSharedValues);
      }
    }
    if (dataBlockType.variant === DataBlockVariant.Visit && visitId) {
      const visitSharedValues = await getVisitValues(
        patientId,
        visitId,
        previousDataBlock.visitId!,
        configData,
        dataBlockTypes,
        openCopyValueForm,
      );
      if (visitSharedValues) {
        setSharedValues(visitSharedValues);
      }
    }
  } catch (error) {
    await modals.showError({ text: parseError(error) });
  }
}
